import { isDeviceMode, type DeviceMode } from "@greg-chick/shared";

/**
 * 📱 **이 기기에서 보호자가 고른 화면** — 🧸 아이 화면 / 🏠 모든 메뉴.
 *
 * ## 왜 이 기기에만 두나
 * 운영자 설정은 **전체에 하나**다. 그런데 어린이집 태블릿과 집에서 형이 쓰는 폰은 답이 다르다.
 * 서버로 보내면 「누가 어떤 기기를 쓰는지」가 생긴다 — 우리는 그런 걸 안 만든다(불변식 1).
 * 그래서 이 기기 저장소에만 둔다. 브라우저 데이터를 지우면 운영자 설정으로 돌아간다.
 *
 * ## 🔴 바꾸는 자리는 어른 확인 뒤에만
 * 여기는 **값을 담는 곳**일 뿐이고, 고르는 화면은 `/family`(보호자 화면) 안에 둔다.
 * 아이가 눌러서 바꿀 수 있으면 영유아 모드는 장식이다.
 *
 * ## 🔴 바뀌면 **지금 열려 있는 화면**도 바뀌어야 한다
 * 보호자 화면에서 바꾸고 첫 화면으로 돌아왔는데 그대로면, 보호자는 「안 바뀐다」로 겪는다.
 * localStorage 는 스스로 알려 주지 않으므로 여기서 **듣는 사람 목록**을 들고 있는다.
 */
const KEY = "gc_ui_device_mode";

/** 저장소가 막힌 기기(사생활 보호 모드 등)에서도 이 화면 동안은 버틴다. */
let memo: DeviceMode | null | undefined;
const listeners = new Set<() => void>();

/** 보호자가 고른 것. 안 골랐으면 `undefined`(운영자 설정을 따른다). */
export function getDeviceMode(): DeviceMode | undefined {
  if (memo !== undefined) return memo ?? undefined;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    // 저장소가 막힌 기기(사생활 보호 모드) — **안 고른 것으로 본다**.
    // 여기서 말할 것은 없다: 못 읽어도 운영자 설정이라는 멀쩡한 답이 있다(불변식 3).
    raw = null;
  }
  memo = isDeviceMode(raw) ? raw : null; // 🔴 모르는 값은 버린다(밖에서 온 값 — 불변식 7)
  return memo ?? undefined;
}

/**
 * 고르기. `undefined` 면 「운영자 설정을 따른다」로 되돌린다.
 *
 * 🔴 **못 남겼으면 그렇다고 돌려준다**(`saved: false`). 읽기와 다르다 — 읽기가 실패하면
 * 운영자 설정이라는 멀쩡한 답이 있지만, **쓰기가 실패하면 보호자가 방금 한 선택이 사라진다.**
 * 화면은 바뀌어 있으니(memo) 보호자는 됐다고 믿고 나가고, 다음에 열면 되돌아가 있다.
 * 왜인지 알 길이 없다. 「처음부터 다시 하기」가 `failed` 를 돌려주는 것과 같은 이유다.
 */
export function setDeviceMode(mode: DeviceMode | undefined): { saved: boolean } {
  memo = mode ?? null;
  let saved = true;
  try {
    if (mode) localStorage.setItem(KEY, mode);
    else localStorage.removeItem(KEY);
  } catch {
    saved = false; // 이 화면 동안은 memo 로 버틴다 — 다만 「기억했다」고 하지 않는다
  }
  listeners.forEach((f) => f());
  return { saved };
}

/** 바뀌면 알려 준다(`useSyncExternalStore` 용). */
export function subscribeDeviceMode(f: () => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}

/** 검사에서 되돌리려고. */
export function resetDeviceModeForTest(): void {
  memo = undefined;
  listeners.clear();
}
